'use client'

import { useCallback, useEffect, useMemo, useRef } from 'react'

const LONG_PRESS_DELAY_MS = 500
const MOVE_TOLERANCE_PX = 10

type LongPressCallback = (clientX: number, clientY: number) => void

/**
 * Turns a stationary touch into the same context-menu action used by right-click.
 * Small finger jitter is tolerated; an intentional scroll cancels the timer.
 */
export function useLongPressContextMenu() {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startRef = useRef({ x: 0, y: 0 })
  const firedRef = useRef(false)

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
  }, [])

  useEffect(() => clearTimer, [clearTimer])

  const onTouchStart = useCallback(
    (event: React.TouchEvent, onLongPress: LongPressCallback) => {
      clearTimer()
      firedRef.current = false
      const touch = event.touches[0]
      if (!touch) return
      const { clientX, clientY } = touch
      startRef.current = { x: clientX, y: clientY }
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        firedRef.current = true
        onLongPress(clientX, clientY)
      }, LONG_PRESS_DELAY_MS)
    },
    [clearTimer]
  )

  const onTouchMove = useCallback(
    (event: React.TouchEvent) => {
      const touch = event.touches[0]
      if (!touch) return
      if (
        Math.abs(touch.clientX - startRef.current.x) > MOVE_TOLERANCE_PX ||
        Math.abs(touch.clientY - startRef.current.y) > MOVE_TOLERANCE_PX
      ) {
        clearTimer()
      }
    },
    [clearTimer]
  )

  const onTouchEnd = useCallback(
    (event: React.TouchEvent) => {
      clearTimer()
      if (firedRef.current) event.preventDefault()
    },
    [clearTimer]
  )

  const consumeClickSuppression = useCallback(() => {
    if (!firedRef.current) return false
    firedRef.current = false
    return true
  }, [])

  // Spread onto an element that already handles `onContextMenu`: a long press dispatches a real
  // `contextmenu` event at the touch point, so the right-click handler runs unchanged. iOS Safari
  // never fires `contextmenu` from a long press by itself, so without this those menus are
  // unreachable on an iPhone.
  const contextMenuTouchHandlers = useMemo(
    () => ({
      onTouchStart: (event: React.TouchEvent<HTMLElement>) => {
        const target = event.currentTarget
        onTouchStart(event, (clientX, clientY) => {
          target.dispatchEvent(
            new MouseEvent('contextmenu', { bubbles: true, cancelable: true, clientX, clientY })
          )
        })
      },
      onTouchMove,
      onTouchEnd,
      onTouchCancel: clearTimer,
    }),
    [clearTimer, onTouchEnd, onTouchMove, onTouchStart]
  )

  return useMemo(
    () => ({
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel: clearTimer,
      consumeClickSuppression,
      contextMenuTouchHandlers,
    }),
    [
      clearTimer,
      consumeClickSuppression,
      contextMenuTouchHandlers,
      onTouchEnd,
      onTouchMove,
      onTouchStart,
    ]
  )
}
